import React, { useMemo } from 'react'
import { useTable, Column } from 'react-table'
import styled from 'styled-components'
import { GateObservation, GateFeedColumnKey, SortingAction } from '../models/client/observation'
import { TableRow, TableHeadCell, TableCell, Table } from './Table'
import { Spacer } from './Spacer'
import { Icon } from './Icon/index'
import { color } from '../styles'
import { SPACING, TRANSITION_TIME_MD_MS } from '../styles/mixins/constants'
import { TableSkeleton } from './TableSkeleton'
import { Tooltip } from './Tooltip'
import { getColumnDirectory } from '../content/tableTemplates'
import { NoneMessage } from './NoneMessage'
import { ObservationSort } from '../clients/type'
import { Popover } from './Popover'

export type ViewableImage = {
  images: string[]
  path: string
  imagePaths: string[]
  observationId: string
  timezone?: string
}

type Props = {
  observations: GateObservation[]
  isLoading?: boolean
  columnKeys: Array<GateFeedColumnKey>
  sort: ObservationSort | null
  disableDuplicate?: boolean
  disableEditTableColumns?: boolean
  disableEditTableRow?: boolean
  renderExitStatus?: boolean
  onViewImage: (image: ViewableImage) => void
  onSettingsClick?: () => void
  onEdit: (item: GateObservation) => void
  onDuplicate: (item: GateObservation) => void
  onDownload: () => void
  onControlClick: () => void
  onSort: SortingAction
}

export const TableGateFeed = (props: Props): JSX.Element => {
  const {
    sort,
    observations,
    isLoading,
    columnKeys,
    disableDuplicate,
    disableEditTableColumns,
    disableEditTableRow,
    renderExitStatus,
    onViewImage,
    onEdit,
    onDuplicate,
    onDownload,
    onControlClick,
    onSort,
  } = props

  const data = useMemo(
    () =>
      observations.map((observation) => {
        return {
          ...observation,
          hasSeal: observation?.hasSeal == null ? '-' : observation?.hasSeal ? 'Yes' : 'No',
        }
      }),
    [observations]
  )
  const columnTemplate: Column<GateObservation>[] = useMemo(
    () => {
      const directory = getColumnDirectory({
        actions: { onViewImage, onSort },
        state: { sort },
        renderExitStatus,
      })
      const nextColumnData: Column<GateObservation>[] = []
      columnKeys.forEach((column) => {
        const nextColumn = directory[column]
        if (!nextColumn) return
        nextColumnData.push(nextColumn)
      })
      return nextColumnData
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnKeys, sort, renderExitStatus]
  )

  const { rows, headers, prepareRow } = useTable({
    data,
    columns: columnTemplate,
  })

  // NOTE: 0px is for the operations col
  const COL_SIZE = `repeat(${headers.length + 1}, 150px)`

  if (isLoading) {
    return <TableSkeleton columnCount={8} rowCount={20} columnSize="repeat(8, 150px)" />
  }

  return (
    <StyledTable>
      <Overflow>
        <HeaderRow columnSize={COL_SIZE} defaultColumnSize="0px" rowSize="50px">
          {headers.map((header, cellIndex) => (
            <TableHeadCell key={`header-${cellIndex}`} {...header.getHeaderProps()}>
              {header.render('Header')}
            </TableHeadCell>
          ))}
          <StickyCell>
            <Tooltip key="sticky-download-tooltip" tipText="Download (CSV)">
              <Icon iconKey="Download" size={5} onClick={() => onDownload()} />
            </Tooltip>

            {!disableEditTableColumns && [
              <Spacer key="edit-table-columns-spacer" size={6} />,
              <Tooltip key="sticky-controls-tooltip" tipText="Settings">
                <Icon iconKey="Controls" size={5} onClick={() => onControlClick()} />
              </Tooltip>,
            ]}
          </StickyCell>
        </HeaderRow>

        {rows.map((row) => {
          prepareRow(row)

          return (
            <RowContainer key={`${row.original.observationId}`} {...row.getRowProps()}>
              <TableRow columnSize={COL_SIZE} onClick={() => {}} defaultColumnSize="0px">
                {row.cells.map((cell, cellIndex) => (
                  <Cell key={`${row.original.observationId}-${cellIndex}`} {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </Cell>
                ))}
                <StickyCell>
                  {disableDuplicate ? null : (
                    <>
                      <Icon iconKey="Copy" size={5} onClick={() => onDuplicate(row.original)} />
                      <Spacer key="sticky-cell-spacer" size={6} />
                    </>
                  )}
                  {!disableEditTableRow && (
                    <Icon iconKey="Info" size={5} onClick={() => onEdit(row.original)} />
                  )}
                  &nbsp;
                  {[...row.original.tags].includes('manual') &&
                    ![row.original.meta.source].includes('ReTool script') && (
                      <Tooltip placement="top" tipText="Manually Created">
                        <Icon iconKey="FileMarkdownOutlined" color="danger" size={5} />
                      </Tooltip>
                    )}
                  {[row.original.meta.source].includes('ReTool script') && (
                    <Tooltip placement="top" tipText="NavTrac Reconciliation">
                      <Icon iconKey="FileMarkdownOutlined" color="blue" size={5} />
                    </Tooltip>
                  )}
                </StickyCell>
              </TableRow>
            </RowContainer>
          )
        })}
      </Overflow>
      {rows.length === 0 ? (
        <NoneContainer>
          <NoneMessage message="No Data" />
        </NoneContainer>
      ) : (
        <div />
      )}
    </StyledTable>
  )
}

const HeaderRow = styled(TableRow)`
  position: sticky;
  top: 0;
  z-index: 500;
`

const StyledTable = styled(Table)`
  min-height: 500px;
`

const Cell = styled(TableCell)`
  height: 100px;
`

const RowContainer = styled.div`
  position: relative;
  min-width: 0px;
  max-width: min-content;
`

const Overflow = styled.div`
  overflow: auto;
  min-height: 0px;
  flex: 1 1 auto;
  display: flex;
  flex-flow: column;
`

const NoneContainer = styled.div`
  display: flex;
  flex: 1 0 auto;
  height: 400px;
  width: 100%;
`

const StickyCell = styled(TableCell)`
  position: sticky;
  right: 0;
  height: auto;
  justify-content: center;
  flex: 1 1 auto;
  max-width: 150px;
  border-left: 1px solid ${color.border.primary};
`
